import * as React from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { TowingEquipmentAdmin } from "../../../../hooks/useTowingEquipmentAdmin";
import { DimensionInput } from "../../MovableObstacleAdmin/components/DimensionInput";
import { EditFormData } from "../index";

interface Props {
  open: boolean;
  equipment: TowingEquipmentAdmin | null;
  isCreating: boolean;
  onClose: () => void;
  onSave: (data: EditFormData) => Promise<void>;
}

export const EquipmentEditDialog: React.FC<Props> = ({
  open,
  equipment,
  isCreating,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = React.useState<EditFormData>({
    name: "",
    width: "",
    length: "",
    height: "",
    tow_bar_length: "",
    no_footprint: false,
  });

  // Reset form when equipment changes or creating new
  React.useEffect(() => {
    if (isCreating) {
      setFormData({
        name: "",
        width: "",
        length: "",
        height: "",
        tow_bar_length: "",
        no_footprint: false,
      });
    } else if (equipment) {
      setFormData({
        name: equipment.name,
        width: "",
        length: "",
        height: "",
        tow_bar_length: "",
        no_footprint: equipment.no_footprint ?? false,
      });
    }
  }, [equipment, isCreating]);

  const handleChange = (field: keyof EditFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    await onSave(formData);
    // Don't close here - let parent close on success
  };

  const handleClose = () => {
    setFormData({
      name: "",
      width: "",
      length: "",
      height: "",
      tow_bar_length: "",
      no_footprint: false,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isCreating ? t('superAdmin.towingEquipmentEdit.title.create') : t('superAdmin.towingEquipmentEdit.title.edit')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label={t('superAdmin.towingEquipmentEdit.labels.name')}
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder={equipment?.name || t('superAdmin.towingEquipmentEdit.placeholders.name.create')}
            autoFocus
            required={isCreating}
          />
          <Divider />

          {(equipment || isCreating) && (
            <>
              <DimensionInput
                label={t('superAdmin.towingEquipmentEdit.labels.width')}
                currentValue={equipment?.width}
                value={formData.width}
                onChange={(value) => handleChange('width', value)}
                placeholder={isCreating ? t('superAdmin.towingEquipmentEdit.placeholders.width.create') : t('superAdmin.towingEquipmentEdit.placeholders.width.edit')}
                required={isCreating}
              />
              <DimensionInput
                label={t('superAdmin.towingEquipmentEdit.labels.length')}
                currentValue={equipment?.length}
                value={formData.length}
                onChange={(value) => handleChange('length', value)}
                placeholder={isCreating ? t('superAdmin.towingEquipmentEdit.placeholders.length.create') : t('superAdmin.towingEquipmentEdit.placeholders.length.edit')}
                required={isCreating}
              />
              <DimensionInput
                label={t('superAdmin.towingEquipmentEdit.labels.height')}
                currentValue={equipment?.height}
                value={formData.height}
                onChange={(value) => handleChange('height', value)}
                placeholder={isCreating ? t('superAdmin.towingEquipmentEdit.placeholders.height.create') : t('superAdmin.towingEquipmentEdit.placeholders.height.edit')}
                required={isCreating}
              />
            </>
          )}

          <Divider />

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.no_footprint}
                onChange={(e) => handleChange('no_footprint', e.target.checked)}
              />
            }
            label={t('superAdmin.towingEquipmentEdit.labels.noFootprint')}
          />
          <Typography variant="caption" color="textSecondary">
            {t('superAdmin.towingEquipmentEdit.helperText.noFootprint')}
          </Typography>

          <Divider />

          <DimensionInput
            label={t('superAdmin.towingEquipmentEdit.labels.towBar')}
            currentValue={equipment?.tow_bar_length ?? undefined}
            value={formData.tow_bar_length}
            onChange={(value) => handleChange('tow_bar_length', value)}
            placeholder={isCreating ? t('superAdmin.towingEquipmentEdit.placeholders.towBar.create') : t('superAdmin.towingEquipmentEdit.placeholders.towBar.edit')}
            required={false}
          />
        </Stack>
        {!isCreating && (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
            {t('superAdmin.towingEquipmentEdit.helperText.keepCurrent')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('superAdmin.towingEquipmentEdit.buttons.cancel')}</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={
            isCreating && (
              !formData.name ||
              !formData.width ||
              !formData.length ||
              !formData.height
            )
          }
        >
          {isCreating ? t('superAdmin.towingEquipmentEdit.buttons.create') : t('superAdmin.towingEquipmentEdit.buttons.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
